import { of, map, EMPTY, finalize, switchMap, catchError, throwError, shareReplay, type Observable } from 'rxjs';

import { inject } from '@angular/core';
import {
  type HttpEvent,
  type HttpRequest,
  HttpErrorResponse,
  type HttpHandlerFn,
  type HttpInterceptorFn,
} from '@angular/common/http';

import { withBearerToken } from '../utils/with-bearer-token';

import { AUTH_SESSION, ALLOW_REFRESH } from '../tokens';

type RefreshResult = { kind: 'success'; token: string } | { kind: 'failed' };

let refreshInFlight$: Observable<RefreshResult> | null = null;

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authSession = inject(AUTH_SESSION);

  const handleSessionExpired = (): RefreshResult => {
    authSession.handleSessionExpired?.();
    return { kind: 'failed' };
  };

  const getRefreshInFlight = (): Observable<RefreshResult> => {
    if (refreshInFlight$) {
      return refreshInFlight$;
    }

    refreshInFlight$ = authSession.refreshToken().pipe(
      map<string | null, RefreshResult>((token) => {
        if (!token) {
          return handleSessionExpired();
        }

        return { kind: 'success', token };
      }),
      catchError(() => of(handleSessionExpired())),
      finalize(() => {
        refreshInFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return refreshInFlight$;
  };

  const handle401Error = (
    failedReq: HttpRequest<unknown>,
    failedNext: HttpHandlerFn,
  ): Observable<HttpEvent<unknown>> => {
    return getRefreshInFlight().pipe(
      switchMap((result) => {
        if (result.kind === 'failed') {
          return EMPTY;
        }

        return failedNext(withBearerToken(failedReq, result.token));
      }),
    );
  };

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && req.context.get(ALLOW_REFRESH)) {
        return handle401Error(req, next);
      }

      return throwError(() => error);
    }),
  );
};
